import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n, localizeAssetTypeLabel, localizeGeoLabel } from '../i18n';
import { loadGeneralInfoCountries, loadGeneralInfoRegions } from '../api/generalInfoApi';
import type { GeneralInfoPicklistItem } from '../api/generalInfoApi';
import { fetchAssetTypesForBrowse } from '../api/getAssetById';
import { buildAuthorizedHeaders } from '../api/withApiAuth';
import {
  browseFilterAssetTypes,
  browseFilterAssetTypesFallback,
} from '../constants/filterableAssetTypes';
import { assetBrowseShowsRegionFilter } from '../utils/assetBrowseFilters';
import { assetSearchUiStateFromBrowseFilters } from '../utils/assetSearchUiState';
import {
  sortAssetBrowseListRecords,
  type AssetBrowseListSortMode,
} from '../utils/assetBrowseListSort';
import { isAssetKeywordQueryActive } from '../utils/assetKeywordSearch';
import { useUserLocationContext } from '../context/UserLocationContext';
import pageThemeStyles from './menuPageTheme.module.css';

export function useAssetSearchListFilters(keyword: string) {
  const { t, language } = useI18n();
  const { coordinates: viewer } = useUserLocationContext();
  const [countryId, setCountryId] = useState('');
  const [districtId, setDistrictId] = useState('');
  const [assetTypeId, setAssetTypeId] = useState('');
  const [sortMode, setSortMode] = useState<AssetBrowseListSortMode>('distance');
  const [countries, setCountries] = useState<GeneralInfoPicklistItem[]>([]);
  const [countriesStatus, setCountriesStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [countriesError, setCountriesError] = useState<string | null>(null);
  const [regions, setRegions] = useState<GeneralInfoPicklistItem[]>([]);
  const [regionsStatus, setRegionsStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [regionsError, setRegionsError] = useState<string | null>(null);
  const [assetTypes, setAssetTypes] = useState<GeneralInfoPicklistItem[]>([]);
  const [assetTypesCatalogFull, setAssetTypesCatalogFull] = useState<GeneralInfoPicklistItem[]>([]);
  const [assetTypesStatus, setAssetTypesStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [assetTypesError, setAssetTypesError] = useState<string | null>(null);

  const showRegion = assetBrowseShowsRegionFilter(countryId);

  useEffect(() => {
    if (!showRegion && districtId !== '') {
      setDistrictId('');
    }
  }, [showRegion, districtId]);

  useEffect(() => {
    let cancelled = false;
    setCountriesStatus('loading');
    setCountriesError(null);
    void (async () => {
      try {
        const headers = await buildAuthorizedHeaders({ Accept: 'application/json' });
        const list = await loadGeneralInfoCountries(headers);
        if (!cancelled) {
          setCountries(list);
          setCountriesStatus('ready');
        }
      } catch (err) {
        if (!cancelled) {
          setCountries([]);
          setCountriesStatus('error');
          setCountriesError(err instanceof Error ? err.message : t('assets.failedCountries'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    setAssetTypesStatus('loading');
    setAssetTypesError(null);
    void (async () => {
      try {
        const list = await fetchAssetTypesForBrowse();
        if (!cancelled) {
          setAssetTypesCatalogFull(list);
          setAssetTypes(browseFilterAssetTypes(list));
          setAssetTypesStatus('ready');
        }
      } catch (err) {
        if (!cancelled) {
          const fallback = browseFilterAssetTypesFallback();
          setAssetTypesCatalogFull(fallback);
          setAssetTypes(fallback);
          setAssetTypesStatus('error');
          setAssetTypesError(err instanceof Error ? err.message : t('assets.failedAssetTypes'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    const id = Number(countryId);
    if (!showRegion || !Number.isFinite(id)) {
      setRegions([]);
      setRegionsStatus('idle');
      setRegionsError(null);
      return;
    }
    let cancelled = false;
    setRegionsStatus('loading');
    setRegionsError(null);
    void (async () => {
      try {
        const headers = await buildAuthorizedHeaders({ Accept: 'application/json' });
        const list = await loadGeneralInfoRegions(id, headers);
        if (!cancelled) {
          setRegions(list);
          setRegionsStatus('ready');
        }
      } catch (err) {
        if (!cancelled) {
          setRegions([]);
          setRegionsStatus('error');
          setRegionsError(err instanceof Error ? err.message : t('assets.failedDistricts'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [countryId, showRegion, t]);

  const countryLabel = countries.find((item) => String(item.id) === countryId)?.label ?? null;
  const districtLabel = regions.find((item) => String(item.id) === districtId)?.label ?? null;
  const typeLabel = assetTypes.find((item) => String(item.id) === assetTypeId)?.label ?? null;
  const trimmedKeyword = keyword.trim();

  const uiState = useMemo(
    () =>
      assetSearchUiStateFromBrowseFilters({
        countryId,
        districtId,
        assetTypeId,
        text: isAssetKeywordQueryActive(trimmedKeyword) ? trimmedKeyword : undefined,
        assetTypeLabel: typeLabel,
        countryLabel,
        districtLabel,
        assetTypes,
        assetTypesCatalogFull,
      }),
    [
      countryId,
      districtId,
      assetTypeId,
      trimmedKeyword,
      typeLabel,
      countryLabel,
      districtLabel,
      assetTypes,
      assetTypesCatalogFull,
    ],
  );

  const applySort = useCallback(
    (rows: Record<string, unknown>[], keywordActive: boolean) => {
      if (keywordActive) {
        return rows;
      }
      return sortAssetBrowseListRecords(rows, sortMode, { viewer, language });
    },
    [language, sortMode, viewer],
  );

  return {
    uiState,
    sortMode,
    applySort,
    countryId,
    setCountryId,
    districtId,
    setDistrictId,
    assetTypeId,
    setAssetTypeId,
    setSortMode,
    countries,
    countriesStatus,
    countriesError,
    regions,
    regionsStatus,
    regionsError,
    assetTypes,
    assetTypesStatus,
    assetTypesError,
    showRegion,
  };
}

type AssetSearchListFiltersProps = {
  filters: ReturnType<typeof useAssetSearchListFilters>;
  resultCount?: { count: number; total: number; expanding?: boolean } | null;
};

export function AssetSearchListFilters({ filters, resultCount }: AssetSearchListFiltersProps) {
  const { t, language } = useI18n();
  const menuId = 'asset-search-list-filters';

  return (
    <div className={pageThemeStyles.browseFiltersToolbar}>
      <details className={pageThemeStyles.browseFilters}>
        <summary className={pageThemeStyles.browseFiltersSummary}>{t('assets.filter.listFiltersTitle')}</summary>
        <div className={pageThemeStyles.browseFiltersGrid}>
          <label htmlFor={`${menuId}-country`}>
            <span>{t('assets.filter.country')}</span>
            <select
              id={`${menuId}-country`}
              value={filters.countryId}
              onChange={(event) => {
                filters.setCountryId(event.target.value);
                filters.setDistrictId('');
              }}
              disabled={filters.countriesStatus === 'loading'}
            >
              <option value="">{t('assets.filter.allCountries')}</option>
              {filters.countries.map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {localizeGeoLabel(item.label, language)}
                </option>
              ))}
            </select>
            {filters.countriesStatus === 'error' && filters.countriesError ? (
              <span role="alert">{filters.countriesError}</span>
            ) : null}
          </label>
          {filters.showRegion ? (
            <label htmlFor={`${menuId}-district`}>
              <span>{t('assets.filter.region')}</span>
              <select
                id={`${menuId}-district`}
                value={filters.districtId}
                onChange={(event) => filters.setDistrictId(event.target.value)}
                disabled={
                  filters.countryId === '' ||
                  filters.regionsStatus === 'loading' ||
                  (filters.regionsStatus === 'ready' && filters.regions.length === 0)
                }
              >
                <option value="">
                  {filters.countryId === ''
                    ? t('assets.filter.selectCountryFirst')
                    : filters.regionsStatus === 'loading'
                      ? t('assets.filter.loadingRegions')
                      : filters.regions.length === 0
                        ? t('assets.filter.noRegions')
                        : t('assets.filter.allRegions')}
                </option>
                {filters.regions.map((item) => (
                  <option key={item.id} value={String(item.id)}>
                    {localizeGeoLabel(item.label, language)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label htmlFor={`${menuId}-sort`}>
            <span>{t('assets.filter.sort')}</span>
            <select
              id={`${menuId}-sort`}
              value={filters.sortMode}
              onChange={(event) => filters.setSortMode(event.target.value as AssetBrowseListSortMode)}
            >
              <option value="distance">{t('assets.filter.sortDistance')}</option>
              <option value="name">{t('assets.filter.sortName')}</option>
            </select>
          </label>
          <label htmlFor={`${menuId}-type`}>
            <span>{t('assets.filter.assetType')}</span>
            <select
              id={`${menuId}-type`}
              value={filters.assetTypeId}
              onChange={(event) => filters.setAssetTypeId(event.target.value)}
              disabled={filters.assetTypesStatus === 'loading'}
            >
              <option value="">{t('assets.filter.allTypes')}</option>
              {filters.assetTypes.map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {localizeAssetTypeLabel(item.label, language, t)}
                </option>
              ))}
            </select>
            {filters.assetTypesStatus === 'error' && filters.assetTypesError ? (
              <span role="alert">{filters.assetTypesError}</span>
            ) : null}
          </label>
        </div>
      </details>
      {resultCount && (resultCount.total > 0 || resultCount.expanding) ? (
        <p className={pageThemeStyles.browseResultCount} role="status">
          {resultCount.expanding
            ? t('assets.search.resultCountPartial', { count: resultCount.count })
            : t('assets.search.resultCount', {
                count: resultCount.count,
                total: resultCount.total,
              })}
          {resultCount.expanding ? (
            <span className={pageThemeStyles.browseLoadingMore}>
              {' '}
              {t('assets.search.loadingMoreListings')}
            </span>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
